import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import '../../styles/DogSurveysList.css';
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";

const OwnerSurveysList = ({ surveys, dogId }) => {
    const [expandedSurvey, setExpandedSurvey] = useState(null);
    const [surveyResponses, setSurveyResponses] = useState({});
    const [responses, setResponses] = useState({});
    const [loadingResponses, setLoadingResponses] = useState({});
    const [submitting, setSubmitting] = useState({});
    const [error, setError] = useState('');

    // Debug logging with safeguards
    useEffect(() => {
        console.log("Surveys received:", surveys);
        console.log("Dog ID:", dogId);
    }, [surveys, dogId]);

    useEffect(() => {
        if (expandedSurvey && dogId) {
            fetchSurveyResponses(expandedSurvey, dogId);
        }
    }, [expandedSurvey, dogId]);

    const fetchSurveyResponses = async (surveyId, dogId) => {
        if (!surveyId || !dogId) {
            console.error("Missing surveyId or dogId for fetching responses");
            return;
        }

        if (surveyResponses[surveyId]) {
            // Already fetched
            return;
        }

        setLoadingResponses(prev => ({
            ...prev,
            [surveyId]: true
        }));

        setError('');
        console.log(`Fetching responses for survey ${surveyId} and dog ${dogId}`);

        try {
            const response = await axios.get(`/api/v1/survey-response/dog/${dogId}/survey/${surveyId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            console.log("Response data:", response.data);
            const responseData = response.data;
            setSurveyResponses(prev => ({
                ...prev,
                [surveyId]: responseData
            }));

            // Initialize response state if there are existing responses
            if (responseData && responseData.length > 0) {
                const responseMap = {};

                responseData.forEach(response => {
                    // Find the survey
                    const survey = Array.isArray(surveys) ?
                        surveys.find(s => s.id === surveyId) : null;

                    // Find the question from the survey questions
                    const question = survey?.questions?.find(q => q.id === response.questionId);

                    if (!question) {
                        console.log(`Question with ID ${response.questionId} not found`);
                        return;
                    }

                    if (question.questionType === 'MULTIPLE_CHOICE') {
                        responseMap[response.questionId] = response.multipleChoiceAnswer || [];
                    } else if (question.questionType === 'DATE') {
                        responseMap[response.questionId] = response.dateAnswer || '';
                    } else if (question.questionType === 'SINGLE_CHOICE') {
                        responseMap[response.questionId] = response.singleChoiceAnswer || '';
                    } else if (question.questionType === 'CHOICE_WITH_TEXT') {
                        responseMap[response.questionId] = {
                            selectedOption: response.singleChoiceAnswer !== null ? response.singleChoiceAnswer : '',
                            customText: response.textAnswer !== null ? response.textAnswer : ''
                        };
                    } else {
                        responseMap[response.questionId] = response.textAnswer || '';
                    }
                });

                setResponses(prev => ({
                    ...prev,
                    [surveyId]: responseMap
                }));
            } else {
                // Initialize empty responses
                const survey = Array.isArray(surveys) ?
                    surveys.find(s => s.id === surveyId) : null;

                if (survey && Array.isArray(survey.questions)) {
                    const emptyResponses = {};
                    survey.questions.forEach(question => {
                        if (question.questionType === 'MULTIPLE_CHOICE') {
                            emptyResponses[question.id] = [];
                        } else if (question.questionType === 'CHOICE_WITH_TEXT') {
                            // Initialize with empty strings, not null values
                            emptyResponses[question.id] = {
                                selectedOption: '',
                                customText: ''
                            };
                        } else {
                            emptyResponses[question.id] = '';
                        }
                    });

                    setResponses(prev => ({
                        ...prev,
                        [surveyId]: emptyResponses
                    }));
                }
            }
        } catch (err) {
            console.error(`Error fetching responses for survey ${surveyId}:`, err);

            // Initialize empty responses
            const survey = Array.isArray(surveys) ?
                surveys.find(s => s.id === surveyId) : null;

            if (survey && Array.isArray(survey.questions)) {
                const emptyResponses = {};
                survey.questions.forEach(question => {
                    if (question.questionType === 'MULTIPLE_CHOICE') {
                        emptyResponses[question.id] = [];
                    } else if (question.questionType === 'CHOICE_WITH_TEXT') {
                        emptyResponses[question.id] = {
                            selectedOption: '',
                            customText: ''
                        };
                    } else {
                        emptyResponses[question.id] = '';
                    }
                });

                setResponses(prev => ({
                    ...prev,
                    [surveyId]: emptyResponses
                }));
            }

            setSurveyResponses(prev => ({
                ...prev,
                [surveyId]: []
            }));
        } finally {
            setLoadingResponses(prev => ({
                ...prev,
                [surveyId]: false
            }));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Brak daty';
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const toggleSurveyExpand = (surveyId) => {
        setExpandedSurvey(expandedSurvey === surveyId ? null : surveyId);
    };

    const getResponseForQuestion = (questionId, surveyId) => {
        if (!surveyResponses[surveyId] || surveyResponses[surveyId].length === 0) return null;
        return surveyResponses[surveyId].find(response => response.questionId === questionId);
    };

    const handleResponseChange = (surveyId, questionId, value) => {
        setResponses(prev => ({
            ...prev,
            [surveyId]: {
                ...prev[surveyId],
                [questionId]: value
            }
        }));
    };

    const handleMultipleChoiceChange = (surveyId, questionId, option) => {
        setResponses(prev => {
            const currentResponses = prev[surveyId] || {};
            // Initialize as empty array if not exists or not an array
            const current = Array.isArray(currentResponses[questionId]) ? currentResponses[questionId] : [];

            return {
                ...prev,
                [surveyId]: {
                    ...currentResponses,
                    [questionId]: current.includes(option)
                        ? current.filter(o => o !== option)
                        : [...current, option]
                }
            };
        });
    };

    const validateResponses = (surveyId) => {
        const survey = Array.isArray(surveys) ?
            surveys.find(s => s.id === surveyId) : null;

        if (!survey || !Array.isArray(survey.questions)) return false;

        // Check if all required questions have answers
        for (const question of survey.questions) {
            const response = responses[surveyId]?.[question.id];

            if (question.required) {
                if (!response) {
                    alert(`Proszę odpowiedzieć na pytanie: ${question.questionText}`);
                    return false;
                }

                if (question.questionType === 'MULTIPLE_CHOICE' &&
                    (!response || response.length === 0)) {
                    alert(`Proszę wybrać przynajmniej jedną opcję dla pytania: ${question.questionText}`);
                    return false;
                }

                if (question.questionType === 'CHOICE_WITH_TEXT') {
                    // For CHOICE_WITH_TEXT, consider the response valid if either field is filled
                    // Check if the response exists and at least one of selectedOption or customText has content
                    if (!response || ((!response.selectedOption || response.selectedOption === '') &&
                        (!response.customText || response.customText === ''))) {
                        alert(`Proszę odpowiedzieć na pytanie: ${question.questionText}`);
                        return false;
                    }
                }
            }
        }

        return true;
    };

    const handleSubmitSurvey = async (surveyId) => {
        if (!validateResponses(surveyId)) {
            return;
        }

        setSubmitting(prev => ({
            ...prev,
            [surveyId]: true
        }));

        try {
            const survey = Array.isArray(surveys) ?
                surveys.find(s => s.id === surveyId) : null;

            if (!survey || !Array.isArray(survey.questions)) {
                throw new Error('Survey not found or invalid');
            }

            // Prepare responses in the format expected by the backend
            const surveyResponses = survey.questions.map(question => {
                const response = responses[surveyId][question.id];

                if (question.questionType === 'CHOICE_WITH_TEXT') {
                    // Fix: For CHOICE_WITH_TEXT, always set textAnswer with either customText or selectedOption
                    let textAnswer = "";

                    // If customText is provided, use it
                    if (response.customText && response.customText.trim() !== "") {
                        textAnswer = response.customText;
                    }
                    // Otherwise, if a selectedOption is provided, use that as the textAnswer
                    else if (response.selectedOption && response.selectedOption.trim() !== "") {
                        textAnswer = response.selectedOption;
                    }

                    return {
                        questionId: question.id,
                        textAnswer: textAnswer,
                        dateAnswer: null,
                        singleChoiceAnswer: response.selectedOption !== undefined ? response.selectedOption : "",
                        multipleChoiceAnswer: null
                    };
                } else {
                    return {
                        questionId: question.id,
                        textAnswer: question.questionType === 'TEXT' ? (response || "") : null,
                        dateAnswer: question.questionType === 'DATE' ? response : null,
                        singleChoiceAnswer: question.questionType === 'SINGLE_CHOICE' ? (response || "") : null,
                        multipleChoiceAnswer: question.questionType === 'MULTIPLE_CHOICE' ? (response || []) : null
                    };
                }
            });

            const submitData = {
                surveyId,
                dogId,
                responses: surveyResponses
            };

            console.log("Submitting survey responses:", submitData);

            await axios.post('/api/v1/survey-response/submit', submitData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            alert('Odpowiedzi zostały zapisane!');

            // Refresh the responses
            setSurveyResponses(prev => ({
                ...prev,
                [surveyId]: null // Clear cached responses to force a refresh
            }));
            fetchSurveyResponses(surveyId, dogId);
        } catch (err) {
            console.error('Error submitting responses:', err);
            alert('Błąd podczas zapisywania odpowiedzi. Spróbuj ponownie.');
        } finally {
            setSubmitting(prev => ({
                ...prev,
                [surveyId]: false
            }));
        }
    };

    const hasExistingResponses = (surveyId) => {
        return surveyResponses[surveyId] && surveyResponses[surveyId].length > 0;
    };

    const renderResponseValue = (response, questionType) => {
        if (!response) return <span className="owner-survey-no-response">Brak odpowiedzi</span>;

        switch (questionType) {
            case 'TEXT':
                return response.textAnswer || <span className="owner-survey-no-response">Brak odpowiedzi</span>;
            case 'DATE':
                return response.dateAnswer ? formatDate(response.dateAnswer) : <span className="owner-survey-no-response">Brak odpowiedzi</span>;
            case 'SINGLE_CHOICE':
                return response.singleChoiceAnswer || <span className="owner-survey-no-response">Brak odpowiedzi</span>;
            case 'MULTIPLE_CHOICE':
                return response.multipleChoiceAnswer && response.multipleChoiceAnswer.length > 0 ? (
                    <ul className="owner-survey-multiple-choice-list">
                        {response.multipleChoiceAnswer.map((option, idx) => (
                            <li key={idx}>{option}</li>
                        ))}
                    </ul>
                ) : <span className="owner-survey-no-response">Brak odpowiedzi</span>;
            case 'CHOICE_WITH_TEXT':
                return (
                    <div className="owner-survey-choice-with-text-response">
                        {response.singleChoiceAnswer && (
                            <div className="owner-survey-selected-option">Wybór: {response.singleChoiceAnswer}</div>
                        )}
                        {response.textAnswer && (
                            <div className="owner-survey-custom-text">Tekst: {response.textAnswer}</div>
                        )}
                        {!response.singleChoiceAnswer && !response.textAnswer && (
                            <span className="owner-survey-no-response">Brak odpowiedzi</span>
                        )}
                    </div>
                );
            default:
                return <span className="owner-survey-no-response">Brak odpowiedzi</span>;
        }
    };

    // Handle case when surveys is null, undefined, or not an array
    if (!surveys) {
        console.log("No surveys data received");
        return (
            <div className="owner-empty-surveys-state">
                <p className="owner-empty-surveys-text">Ładowanie ankiet...</p>
                <img src="/petboard-empty.png" alt="Loading surveys" className="owner-empty-surveys-image" />
            </div>
        );
    }

    // Ensure surveys is an array
    const surveysArray = Array.isArray(surveys) ? surveys : [];

    if (surveysArray.length === 0) {
        console.log("Empty surveys array");
        return (
            <div className="owner-empty-surveys-state">
                <p className="owner-empty-surveys-text">Brak przypisanych ankiet.</p>
                <img src="/petboard-empty.png" alt="No surveys" className="owner-empty-surveys-image" />
            </div>
        );
    }

    console.log("Rendering surveys list:", surveysArray);

    return (
        <div className="owner-surveys-container">
            <div className="owner-surveys-list">
                {surveysArray.map(survey => (
                    <div
                        key={survey.id}
                        className={`owner-survey-item ${expandedSurvey === survey.id ? 'owner-survey-expanded' : ''}`}
                    >
                        <div className="owner-survey-header" onClick={() => toggleSurveyExpand(survey.id)}>
                            <div className="owner-survey-info">
                                <h3 className="owner-survey-name">{survey.title}</h3>
                                <div className="owner-survey-meta">
                                    {survey.createdAt && (
                                        <span className="owner-survey-date-badge">
                                            Utworzono: {formatDate(survey.createdAt)}
                                        </span>
                                    )}
                                    <span className="owner-survey-questions-badge">
                                        Liczba pytań: {survey.questions ? survey.questions.length : 0}
                                    </span>
                                    {hasExistingResponses(survey.id) && (
                                        <span className="owner-survey-status-badge filled">
                                            Status: Wypełniona
                                        </span>
                                    )}
                                    {!hasExistingResponses(survey.id) && surveyResponses[survey.id] !== undefined && (
                                        <span className="owner-survey-status-badge unfilled">
                                            Status: Niewypełniona
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="owner-survey-actions">
                                {expandedSurvey === survey.id ? (
                                    <IoChevronUpOutline
                                        size={20}
                                        className="owner-survey-expand-icon owner-survey-expanded"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSurveyExpand(survey.id);
                                        }}
                                        title="Ukryj szczegóły"
                                    />
                                ) : (
                                    <IoChevronDownOutline
                                        size={20}
                                        className="owner-survey-expand-icon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSurveyExpand(survey.id);
                                        }}
                                        title="Pokaż szczegóły"
                                    />
                                )}
                            </div>
                        </div>

                        {expandedSurvey === survey.id && (
                            <div className="owner-survey-details" onClick={(e) => e.stopPropagation()}>
                                <div className="owner-survey-details-content">
                                    {loadingResponses[survey.id] ? (
                                        <div className="owner-survey-loading-responses">
                                            <p>Ładowanie ankiety...</p>
                                        </div>
                                    ) : error ? (
                                        <div className="owner-survey-error-message">
                                            <p>{error}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="owner-survey-questions-list">
                                                {survey.questions && Array.isArray(survey.questions) ?
                                                    survey.questions
                                                        .sort((a, b) => a.questionIndex - b.questionIndex)
                                                        .map((question, index) => {
                                                            const existingResponse = getResponseForQuestion(question.id, survey.id);
                                                            const hasResponse = !!existingResponse;

                                                            return (
                                                                <div key={question.id} className="owner-survey-question-item">
                                                                    <div className="owner-survey-question-header">
                                                                        <div className="owner-survey-question-number">{index + 1}.</div>
                                                                        <div className="owner-survey-question-text">
                                                                            {question.questionText}
                                                                            {question.required && !hasResponse && <span className="owner-survey-required-mark">*</span>}
                                                                        </div>
                                                                        <div className="owner-survey-question-type">
                                                                            {question.questionType === 'TEXT' && 'Tekst'}
                                                                            {question.questionType === 'DATE' && 'Data'}
                                                                            {question.questionType === 'SINGLE_CHOICE' && 'Pojedynczy wybór'}
                                                                            {question.questionType === 'MULTIPLE_CHOICE' && 'Wielokrotny wybór'}
                                                                            {question.questionType === 'CHOICE_WITH_TEXT' && 'Wybór + tekst'}
                                                                        </div>
                                                                    </div>

                                                                    <div className="owner-survey-question-content">
                                                                        {/* If there's an existing response, show it in the same style as DogSurveysList */}
                                                                        {hasResponse ? (
                                                                            <>
                                                                                {(question.questionType === 'SINGLE_CHOICE' ||
                                                                                        question.questionType === 'MULTIPLE_CHOICE' ||
                                                                                        question.questionType === 'CHOICE_WITH_TEXT') &&
                                                                                    question.answerOptions &&
                                                                                    question.answerOptions.length > 0 && (
                                                                                        <div className="owner-survey-answer-options">
                                                                                            <div className="owner-survey-options-label">Dostępne opcje:</div>
                                                                                            <ul>
                                                                                                {question.answerOptions.map((option, idx) => (
                                                                                                    <li key={idx}>{option}</li>
                                                                                                ))}
                                                                                            </ul>
                                                                                        </div>
                                                                                    )}

                                                                                <div className="owner-survey-response-section">
                                                                                    <div className="owner-survey-response-label">Odpowiedź:</div>
                                                                                    <div className="owner-survey-response-value">
                                                                                        {renderResponseValue(existingResponse, question.questionType)}
                                                                                    </div>
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            /* Otherwise show input fields */
                                                                            <div className="owner-survey-response-input">
                                                                                {question.questionType === "TEXT" && (
                                                                                    <input
                                                                                        type="text"
                                                                                        className="owner-survey-input"
                                                                                        placeholder="Wpisz swoją odpowiedź..."
                                                                                        value={responses[survey.id]?.[question.id] || ""}
                                                                                        onChange={(e) => handleResponseChange(survey.id, question.id, e.target.value)}
                                                                                    />
                                                                                )}

                                                                                {question.questionType === "DATE" && (
                                                                                    <input
                                                                                        type="date"
                                                                                        className="owner-survey-input owner-survey-date-input"
                                                                                        value={responses[survey.id]?.[question.id] || ""}
                                                                                        onChange={(e) => handleResponseChange(survey.id, question.id, e.target.value)}
                                                                                    />
                                                                                )}

                                                                                {question.questionType === "SINGLE_CHOICE" && (
                                                                                    <div className="owner-survey-single-choice">
                                                                                        {question.answerOptions && question.answerOptions.length > 0 ? (
                                                                                            question.answerOptions.map((option, idx) => (
                                                                                                <label key={idx} className="owner-survey-radio-container">
                                                                                                    <input
                                                                                                        type="radio"
                                                                                                        name={`single-${question.id}`}
                                                                                                        value={option}
                                                                                                        checked={responses[survey.id]?.[question.id] === option}
                                                                                                        onChange={() => handleResponseChange(survey.id, question.id, option)}
                                                                                                    />
                                                                                                    <span className="owner-survey-option-text">{option}</span>
                                                                                                </label>
                                                                                            ))
                                                                                        ) : (
                                                                                            <p className="owner-survey-no-options">Brak dostępnych opcji</p>
                                                                                        )}
                                                                                    </div>
                                                                                )}

                                                                                {question.questionType === "MULTIPLE_CHOICE" && (
                                                                                    <div className="owner-survey-multi-choice">
                                                                                        {question.answerOptions && question.answerOptions.length > 0 ? (
                                                                                            question.answerOptions.map((option, idx) => (
                                                                                                <label key={idx} className="owner-survey-checkbox-container">
                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        value={option}
                                                                                                        checked={Array.isArray(responses[survey.id]?.[question.id]) &&
                                                                                                            responses[survey.id]?.[question.id].includes(option)}
                                                                                                        onChange={() => handleMultipleChoiceChange(survey.id, question.id, option)}
                                                                                                    />
                                                                                                    <span className="owner-survey-option-text">{option}</span>
                                                                                                </label>
                                                                                            ))
                                                                                        ) : (
                                                                                            <p className="owner-survey-no-options">Brak dostępnych opcji</p>
                                                                                        )}
                                                                                    </div>
                                                                                )}

                                                                                {question.questionType === "CHOICE_WITH_TEXT" && (
                                                                                    <div className="owner-survey-choice-with-text">
                                                                                        {question.answerOptions && question.answerOptions.length > 0 ? (
                                                                                            <>
                                                                                                <div className="owner-survey-options-section">
                                                                                                    {question.answerOptions.map((option, idx) => (
                                                                                                        <label key={idx} className="owner-survey-radio-container" onClick={(e) => e.stopPropagation()}>
                                                                                                            <input
                                                                                                                type="radio"
                                                                                                                name={`choice-with-text-${question.id}`}
                                                                                                                value={option}
                                                                                                                checked={responses[survey.id]?.[question.id]?.selectedOption === option}
                                                                                                                onChange={() => handleResponseChange(survey.id, question.id, {
                                                                                                                    selectedOption: option,
                                                                                                                    customText: responses[survey.id]?.[question.id]?.customText || ''
                                                                                                                })}
                                                                                                                onClick={(e) => e.stopPropagation()}
                                                                                                            />
                                                                                                            <span className="owner-survey-option-text">{option}</span>
                                                                                                        </label>
                                                                                                    ))}
                                                                                                </div>
                                                                                                <div className="owner-survey-text-section">
                                                                                                    <input
                                                                                                        type="text"
                                                                                                        className="owner-survey-input"
                                                                                                        placeholder="Lub wpisz własną odpowiedź..."
                                                                                                        value={responses[survey.id]?.[question.id]?.customText || ""}
                                                                                                        onChange={(e) => handleResponseChange(survey.id, question.id, {
                                                                                                            selectedOption: responses[survey.id]?.[question.id]?.selectedOption || '',
                                                                                                            customText: e.target.value
                                                                                                        })}
                                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                                    />
                                                                                                </div>
                                                                                            </>
                                                                                        ) : (
                                                                                            <input
                                                                                                type="text"
                                                                                                className="owner-survey-input"
                                                                                                placeholder="Wpisz swoją odpowiedź..."
                                                                                                value={responses[survey.id]?.[question.id]?.customText || ""}
                                                                                                onChange={(e) => handleResponseChange(survey.id, question.id, {
                                                                                                    selectedOption: '',
                                                                                                    customText: e.target.value
                                                                                                })}
                                                                                                onClick={(e) => e.stopPropagation()}
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    : (
                                                        <div className="owner-survey-error-message">
                                                            <p>Nie można załadować pytań</p>
                                                        </div>
                                                    )
                                                }
                                            </div>

                                            {/* Show submit button if there are no responses */}
                                            {!hasExistingResponses(survey.id) && (
                                                <div className="owner-survey-actions-section">
                                                    <button
                                                        className="owner-survey-submit-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSubmitSurvey(survey.id);
                                                        }}
                                                        disabled={submitting[survey.id]}
                                                    >
                                                        {submitting[survey.id] ? 'Zapisywanie...' : 'Zapisz odpowiedzi'}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OwnerSurveysList;